import { Item } from '../domain/item';
import { Location, normalizeLocationName } from '../domain/location';

export interface DocumentLike {
  id: string;
  data(): Record<string, unknown>;
}

const dateString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate(): Date }).toDate().toISOString();
  }
  return fallback;
};

export const mapItemDocument = (document: DocumentLike): Item | null => {
  const data = document.data();
  if (
    typeof data.keywords !== 'string' ||
    typeof data.location !== 'string' ||
    typeof data.qty !== 'number' ||
    !Number.isFinite(data.qty)
  )
    return null;

  const createdAt = dateString(data.createdAt);
  if (!createdAt) return null;

  const lastEditedBy =
    typeof data.lastEditedBy === 'string'
      ? data.lastEditedBy
      : typeof data.actorUserId === 'string'
        ? data.actorUserId
        : typeof data.userId === 'string'
          ? data.userId
          : undefined;

  return {
    id: document.id,
    ...(typeof data.revision === 'number' ? { revision: data.revision } : {}),
    keywords: data.keywords,
    image: typeof data.image === 'string' ? data.image : '',
    location: data.location,
    locationId:
      typeof data.locationId === 'string' ? data.locationId : undefined,
    qty: data.qty,
    createdAt,
    lastEditedAt: dateString(data.lastEditedAt) || undefined,
    lastEditedBy,
    storagePath:
      typeof data.storagePath === 'string' ? data.storagePath : undefined,
    deleted: data.deleted === true,
    captureOperationId:
      typeof data.captureOperationId === 'string'
        ? data.captureOperationId
        : undefined,
    ...(Array.isArray(data.tagIds)
      ? {
          tagIds: data.tagIds.filter(
            (id): id is string => typeof id === 'string',
          ),
        }
      : {}),
  };
};

export const mapActiveItemDocument = (document: DocumentLike): Item | null => {
  const item = mapItemDocument(document);
  return item?.deleted ? null : item;
};

export const mapLocationDocument = (
  document: DocumentLike,
): Location | null => {
  const data = document.data();
  if (typeof data.name !== 'string' || !data.name.trim()) return null;
  const name = data.name.trim();
  const lastEditedBy =
    typeof data.lastEditedBy === 'string'
      ? data.lastEditedBy
      : typeof data.actorUserId === 'string'
        ? data.actorUserId
        : typeof data.userId === 'string'
          ? data.userId
          : undefined;

  return {
    id: document.id,
    name,
    normalizedName:
      typeof data.normalizedName === 'string'
        ? data.normalizedName
        : normalizeLocationName(name),
    description: typeof data.description === 'string' ? data.description : '',
    createdAt: dateString(data.createdAt),
    lastEditedAt: dateString(data.lastEditedAt) || undefined,
    lastEditedBy,
  };
};
